import { type FC, useState } from 'react'
import {
  Building2,
  MapPin,
  TrendingUp,
  DollarSign,
  Clock,
  CheckCircle,
  Edit3,
  Trash2,
  XCircle,
  MoreHorizontal,
} from 'lucide-react'
import { Card } from '../ui/Card'
import { Button } from '../ui/Button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/DropdownMenu'
import { type MarketplaceProperty } from '../../services/api/admin'
import { cn } from '../../lib/utils'
import {
  formatCentsCompact,
  formatHoldPeriodMonths,
  formatReturnRange,
  assetClassBadgeClass,
} from '../../lib/marketplaceFormatters'

export { formatCentsCompact, formatHoldPeriodMonths, formatReturnRange, assetClassBadgeClass }

interface AdminListingCardProps {
  property: MarketplaceProperty
  onEdit: (property: MarketplaceProperty) => void
  onDelete: (id: string) => void
  onTogglePublished: (property: MarketplaceProperty) => void
}

export const AdminListingCard: FC<AdminListingCardProps> = ({
  property,
  onEdit,
  onDelete,
  onTogglePublished,
}) => {
  const heroImage = property.images[0] ?? null
  const assetClass = property.dstAssetClass ?? 'other'
  const location = [property.addressCity, property.addressState].filter(Boolean).join(', ') || '—'
  const [imageLoadFailed, setImageLoadFailed] = useState(false)

  return (
    <Card className="overflow-hidden flex flex-col">
      <div className="relative h-40 bg-muted flex items-center justify-center overflow-hidden">
        {heroImage && !imageLoadFailed ? (
          <img
            src={heroImage}
            alt={property.title}
            className="w-full h-full object-cover"
            onError={() => setImageLoadFailed(true)}
          />
        ) : (
          <Building2 className="h-12 w-12 text-muted-foreground" aria-hidden="true" />
        )}
        <div className="absolute top-2 left-2 flex flex-col gap-1">
          {property.qualifiedStatus === 'qualified' && (
            <span
              className={cn(
                'inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-semibold',
                'bg-emerald-100 dark:bg-emerald-900/40 text-emerald-800 dark:text-emerald-300'
              )}
            >
              <CheckCircle className="h-3 w-3" aria-hidden="true" />
              Qualified
            </span>
          )}
          <span className={cn(
            'inline-flex items-center rounded-full px-2 py-0.5 text-xs font-semibold',
            property.published
              ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300'
              : 'bg-secondary text-foreground'
          )}>
            {property.published ? 'Published' : 'Draft'}
          </span>
        </div>
        <div className="absolute top-2 right-2">
          <span className={cn(
            'inline-flex items-center rounded-full px-2 py-0.5 text-xs font-semibold capitalize',
            assetClassBadgeClass(property.dstAssetClass)
          )}>
            {assetClass.charAt(0).toUpperCase() + assetClass.slice(1)}
          </span>
        </div>
      </div>

      <div className="p-4 flex-1 flex flex-col gap-2">
        <div>
          <h3 className="text-sm font-semibold text-foreground leading-snug line-clamp-2">
            {property.title}
          </h3>
          {property.sponsor && (
            <p className="text-xs text-muted-foreground mt-0.5">{property.sponsor}</p>
          )}
        </div>
        <div className="flex items-center gap-1 text-muted-foreground">
          <MapPin className="h-3 w-3 shrink-0" aria-hidden="true" />
          <span className="text-xs">{location}</span>
        </div>
        <dl className="grid grid-cols-3 gap-2 text-center mt-1">
          <div>
            <dt className="flex items-center justify-center gap-0.5 text-xs text-muted-foreground">
              <TrendingUp className="h-3 w-3" aria-hidden="true" />
              Return
            </dt>
            <dd className="text-xs font-semibold text-emerald-600 dark:text-emerald-400">
              {formatReturnRange(property.dstExpectedReturnMin, property.dstExpectedReturnMax)}
            </dd>
          </div>
          <div>
            <dt className="flex items-center justify-center gap-0.5 text-xs text-muted-foreground">
              <DollarSign className="h-3 w-3" aria-hidden="true" />
              Min
            </dt>
            <dd className="text-xs font-semibold text-foreground">
              {formatCentsCompact(property.dstMinInvestmentCents)}
            </dd>
          </div>
          <div>
            <dt className="flex items-center justify-center gap-0.5 text-xs text-muted-foreground">
              <Clock className="h-3 w-3" aria-hidden="true" />
              Hold
            </dt>
            <dd className="text-xs font-semibold text-foreground">
              {formatHoldPeriodMonths(property.dstHoldPeriodMonths)}
            </dd>
          </div>
        </dl>
      </div>

      <div className="flex items-center justify-between px-4 pb-4 pt-0">
        <span className="text-xs text-muted-foreground">
          {new Date(property.createdAt).toLocaleDateString()}
        </span>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              title="Listing actions"
              className="h-8 w-8"
            >
              <MoreHorizontal className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={() => onEdit(property)}>
              <Edit3 className="h-4 w-4 mr-2" />
              Edit
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => onTogglePublished(property)}>
              {property.published ? (
                <><XCircle className="h-4 w-4 mr-2" />Unpublish</>
              ) : (
                <><CheckCircle className="h-4 w-4 mr-2" />Publish</>
              )}
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onClick={() => onDelete(property.id)}
              className="text-destructive focus:text-destructive"
            >
              <Trash2 className="h-4 w-4 mr-2" />
              Delete
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </Card>
  )
}
